import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedEnemy, EnemyInput, EnemyResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface EnemyStore {
  enemies: SavedEnemy[]
  addEnemy: (enemy: Omit<SavedEnemy, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateEnemy: (id: string, enemy: Partial<SavedEnemy>) => void
  removeEnemy: (id: string) => void
  clearEnemies: () => void
  getEnemy: (id: string) => SavedEnemy | undefined
  toggleFavorite: (id: string) => void
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  fixDuplicateIds: () => boolean
}

const MAX_ENEMIES = 500

export const useEnemyStore = create<EnemyStore>()(
  persist(
    (set, get) => ({
      enemies: [],

      addEnemy: (enemy) => {
        const newEnemy: SavedEnemy = {
          ...enemy,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => {
          const enemies = [newEnemy, ...state.enemies]
          if (enemies.length > MAX_ENEMIES) {
            enemies.pop()
          }
          return { enemies }
        })
      },
      
      // ID重複をチェックして修正する関数
      fixDuplicateIds: () => {
        const { enemies } = get()
        const idMap = new Map<string, number>()
        const duplicates: string[] = []
        
        // 重複IDを検出
        enemies.forEach(enemy => {
          const count = idMap.get(enemy.id) || 0
          idMap.set(enemy.id, count + 1)
          if (count > 0) {
            duplicates.push(enemy.id)
          }
        })
        
        if (duplicates.length === 0) {
          console.log('ID重複はありません')
          return false
        }
        
        console.log(`${duplicates.length}件のID重複を検出しました。修正します...`)
        
        // 重複したIDを持つエネミーに新しいIDを割り当て
        const fixedEnemies = enemies.map((enemy, index) => {
          if (duplicates.includes(enemy.id)) {
            // 2番目以降の重複エネミーに新しいIDを生成
            const firstIndex = enemies.findIndex(e => e.id === enemy.id)
            if (firstIndex !== index) {
              return {
                ...enemy,
                id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
                updatedAt: new Date().toISOString()
              }
            }
          }
          return enemy
        })
        
        set({ enemies: fixedEnemies })
        console.log('ID重複を修正しました')
        return true
      },

      updateEnemy: (id, enemy) => {
        set(state => ({
          enemies: state.enemies.map(e =>
            e.id === id
              ? { ...e, ...enemy, updatedAt: new Date().toISOString() }
              : e
          )
        }))
      },

      removeEnemy: (id) => {
        set(state => ({
          enemies: state.enemies.filter(e => e.id !== id)
        }))
      },

      clearEnemies: () => {
        set({ enemies: [] })
      },

      getEnemy: (id) => {
        return get().enemies.find(e => e.id === id)
      },

      toggleFavorite: (id) => {
        set(state => ({
          enemies: state.enemies.map(e =>
            e.id === id ? { ...e, favorite: !e.favorite } : e
          )
        }))
      },

      exportAsText: () => {
        const enemies = get().enemies
        if (enemies.length === 0) {
          alert('保存されたエネミーがありません。')
          return
        }

        const content = formatEnemiesForExport(enemies)
        downloadTextFile(content, `saved_enemies_${Date.now()}.txt`)
      },

      exportAsJSON: () => {
        const enemies = get().enemies
        if (enemies.length === 0) {
          alert('保存されたエネミーがありません。')
          return
        }

        const exportData = {
          version: '1.0.0',
          type: 'enemies',
          exportDate: new Date().toISOString(),
          count: enemies.length,
          data: enemies
        }
        
        const json = JSON.stringify(exportData, null, 2)
        downloadTextFile(json, `saved_enemies_${Date.now()}.json`, 'application/json')
      },

      exportSingleAsText: (id: string) => {
        const enemy = get().getEnemy(id)
        if (!enemy) {
          alert('エネミーが見つかりません。')
          return
        }

        const content = formatEnemiesForExport([enemy])
        downloadTextFile(content, `enemy_${enemy.result.basicInfo.name}_${Date.now()}.txt`)
      },

      exportSingleAsJSON: (id: string) => {
        const enemy = get().getEnemy(id)
        if (!enemy) {
          alert('エネミーが見つかりません。')
          return
        }

        const exportData = {
          version: '1.0.0',
          type: 'enemy',
          exportDate: new Date().toISOString(),
          data: enemy
        }

        const json = JSON.stringify(exportData, null, 2)
        downloadTextFile(json, `enemy_${enemy.result.basicInfo.name}_${Date.now()}.json`, 'application/json')
      },

      importFromJSON: (jsonString: string) => {
        try {
          const imported = JSON.parse(jsonString)
          
          // 複数の形式をサポート
          let enemies: SavedEnemy[]
          if (imported.type === 'enemy' && imported.data && !Array.isArray(imported.data)) {
            // 単独エネミーの場合
            enemies = [imported.data as SavedEnemy]
          } else {
            // 複数エネミーの場合
            enemies = (
              imported.data?.enemies ||  // VersionManager変換後の形式
              (imported.type === 'enemies' ? imported.data : null) ||  // 個別エクスポート形式
              imported.data ||  // BulkDataManager形式
              (Array.isArray(imported) ? imported : [])  // 直接配列形式
            ) as SavedEnemy[]
          }
          const currentEnemies = get().enemies
          
          // 重複チェック（タイトルとエネミー名で判定）
          const uniqueEnemies = enemies.filter(newEnemy => {
            return !currentEnemies.some(existing => 
              existing.title === newEnemy.title && 
              existing.result.basicInfo.name === newEnemy.result.basicInfo.name
            )
          })
          
          const processedEnemies = uniqueEnemies.map(enemy => {
            // 後方互換性: 古いlevelフィールドからthreatLevelへの変換
            const updatedEnemy = { ...enemy }
            
            // input.levelからinput.threatLevelへの変換
            if (updatedEnemy.input && 'level' in updatedEnemy.input && !updatedEnemy.input.threatLevel) {
              const level = (updatedEnemy.input as any).level
              if (level <= 10) updatedEnemy.input.threatLevel = 'E'
              else if (level <= 25) updatedEnemy.input.threatLevel = 'D'
              else if (level <= 40) updatedEnemy.input.threatLevel = 'C'
              else if (level <= 60) updatedEnemy.input.threatLevel = 'B'
              else if (level <= 80) updatedEnemy.input.threatLevel = 'A'
              else if (level <= 95) updatedEnemy.input.threatLevel = 'S'
              else updatedEnemy.input.threatLevel = 'SS'
              
              delete (updatedEnemy.input as any).level
            }
            
            // result.basicInfo.levelを削除（threatフィールドは既に存在）
            if (updatedEnemy.result?.basicInfo && 'level' in updatedEnemy.result.basicInfo) {
              delete (updatedEnemy.result.basicInfo as any).level
            }
            
            return {
              ...updatedEnemy,
              id: Date.now().toString() + Math.random().toString(36).slice(2, 11),
              createdAt: updatedEnemy.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          })
          
          const combined = [...currentEnemies, ...processedEnemies]
          const limited = combined.slice(0, MAX_ENEMIES)
          
          set({ enemies: limited })
          
          const skippedCount = enemies.length - uniqueEnemies.length
          console.log(`Imported ${uniqueEnemies.length} unique enemies (${skippedCount} duplicates skipped)`)
        } catch (error) {
          console.error('インポートエラー:', error)
          throw new Error('JSONファイルの読み込みに失敗しました')
        }
      }
    }),
    {
      name: 'enemy-storage',
      version: 1,
      storage: createIndexedDBStorage('enemy-storage'),
      partialize: (state) => ({ enemies: state.enemies }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)

// エネミーをテキスト形式にフォーマット
function formatEnemiesForExport(enemies: SavedEnemy[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let text = '=== エネミー・怪異リスト ===\n'
  text += `エクスポート日時: ${date}\n`
  text += `合計: ${enemies.length}体\n`
  text += '='.repeat(50) + '\n\n'

  enemies.forEach((enemy, index) => {
    text += `[${index + 1}] ${enemy.title}\n`
    text += '━'.repeat(50) + '\n'
    
    const { basicInfo, appearance, abilities, behavior, lore, encounter } = enemy.result
    
    text += `【基本情報】\n`
    text += `名前: ${basicInfo.name}\n`
    if (basicInfo.title) text += `二つ名: ${basicInfo.title}\n`
    text += `タイプ: ${basicInfo.type}\n`
    text += `カテゴリ: ${basicInfo.category}\n`
    text += `脅威度: ${basicInfo.threat}\n`
    text += `\n概要: ${basicInfo.summary}\n`
    text += `\n説明:\n${basicInfo.description}\n`
    
    text += `\n【外見】\n`
    text += `${appearance.overallDescription}\n`
    text += `サイズ: ${appearance.size}\n`
    text += `体型: ${appearance.bodyType}\n`
    if (appearance.distinctiveFeatures && appearance.distinctiveFeatures.length > 0) {
      text += `特徴:\n`
      appearance.distinctiveFeatures.forEach(f => text += `  ・${f}\n`)
    }
    
    text += `\n【能力】\n`
    if (abilities.combatCapabilities.attackPatterns) {
      text += `攻撃パターン:\n`
      abilities.combatCapabilities.attackPatterns.forEach(a => {
        text += `  ・${a.name}: ${a.description}\n`
      })
    }
    if (abilities.weaknesses && abilities.weaknesses.length > 0) {
      text += `弱点:\n`
      abilities.weaknesses.forEach(w => text += `  ・${w.type}: ${w.description}\n`)
    }
    
    text += `\n【行動パターン】\n`
    text += `${behavior.aiPattern}\n`
    text += `知能: ${behavior.intelligenceDescription}\n`
    
    text += `\n【伝承】\n`
    if (lore.originStory) {
      text += `起源:\n${lore.originStory.narrative}\n`
    }
    if (lore.legends && lore.legends.length > 0) {
      text += `\n伝説:\n`
      lore.legends.forEach(l => {
        text += `『${l.title}』\n${l.story}\n\n`
      })
    }
    
    text += `\n【遭遇情報】\n`
    if (encounter.conditions) {
      text += `出現場所: ${encounter.conditions.location.join('、')}\n`
      text += `出現時間: ${encounter.conditions.time.join('、')}\n`
    }
    if (encounter.taboos && encounter.taboos.length > 0) {
      text += `禁忌:\n`
      encounter.taboos.forEach(t => text += `  ・${t.action}: ${t.consequence}\n`)
    }
    
    if (enemy.memo) {
      text += `\n【メモ】\n${enemy.memo}\n`
    }
    
    if (enemy.tags && enemy.tags.length > 0) {
      text += `\nタグ: ${enemy.tags.join(', ')}\n`
    }
    
    text += `\n作成日: ${new Date(enemy.createdAt).toLocaleDateString('ja-JP')}\n`
    if (enemy.updatedAt) {
      text += `更新日: ${new Date(enemy.updatedAt).toLocaleDateString('ja-JP')}\n`
    }
    
    text += '\n' + '='.repeat(50) + '\n\n'
  })

  return text
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string, mimeType: string = 'text/plain') {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}